/**
 * AFNO — Streamate affiliate click-tracking codes for OneClick cam links.
 *
 * Streamate's OneClick URL carries an AFNO segment that attributes the click
 * to a placement:
 *
 *   https://{cam}/a/{afno}/landing/click/oc/{jwe}
 *
 * zuul emits the default "1-0" (untracked). We compute this code per click and
 * send it to the backend (GET /api/members/cams?afno=...), which forwards it to
 * zuul so the OneClick URL/token is minted WITH it — letting Streamate attribute
 * both the click AND the join to the right site / placement / device.
 *
 * Format:  "1-{N}"   where 1 is our Streamate campaign prefix and
 *
 *   N = siteId * 1000 + spot * 100 + device * 10 + source
 *
 *     siteId  primetime sites.id (stable, 1..999)
 *     spot    AD_SPOT code below
 *     device  DEVICE code below
 *     source  SOURCE code below (0 = 1-click; the only one for now)
 *
 * The decimal layout keeps each AFNO human-decodable, e.g.
 *   1-88120  ->  site 88 (tiny4k), nav (1), desktop (2), 1-click (0)
 *
 * Because every dimension owns its own digit range, adding a placement or
 * source later does NOT renumber existing codes — tracking stays continuous.
 * Full enumerated sheet: docs/afno-codes.md / docs/afno-codes.csv.
 */

export const AFNO_CAMPAIGN = "1"

/** The SDK's fallback when no placement is known — also what zuul emits by default. */
export const DEFAULT_AFNO = `${AFNO_CAMPAIGN}-0`

/** Ad-spot (placement) codes. Range 1..9. */
export const AD_SPOT = {
  nav: 1, // "Live Cams" link in the member nav (mobile + desktop)
  sidebar: 2, // member sidebar cam links
  // The cam-circles widget renders in three spots, tracked separately:
  "liveCamCircles-home": 3, // members home page
  "liveCamCircles-video": 4, // video page
  "liveCamCircles-channel-video": 5, // channel video page
} as const
export type AdSpot = keyof typeof AD_SPOT

/** Device codes. Range 1..9. */
export const DEVICE = {
  mobile: 1,
  desktop: 2,
} as const
export type Device = keyof typeof DEVICE

/** Traffic-source codes. Range 0..9. Only 1-click exists today. */
export const SOURCE = {
  oneClick: 0,
} as const
export type Source = keyof typeof SOURCE

/**
 * Build the AFNO code for a click context. Returns DEFAULT_AFNO when the
 * site or placement is unknown, so tracking degrades gracefully.
 */
export function afnoFor(
  siteId: number | null | undefined,
  adSpot: AdSpot,
  device: Device,
  source: Source = "oneClick",
): string {
  const spot = AD_SPOT[adSpot]
  const dev = DEVICE[device]
  const src = SOURCE[source]
  if (!siteId || spot === undefined || dev === undefined || src === undefined) {
    return DEFAULT_AFNO
  }
  return `${AFNO_CAMPAIGN}-${siteId * 1000 + spot * 100 + dev * 10 + src}`
}
